import { useEffect, useState } from "react";
import { CancelToken } from "axios";

interface IUseSearchableEntityProps<T> {
  fetchAll: (cancelToken?: CancelToken) => Promise<T[]>;
  searchFn: (search: string) => Promise<T[]>;
  debounceMs?: number;
}

export function useSearch<T>({
  fetchAll,
  searchFn,
  debounceMs = 500,
}: IUseSearchableEntityProps<T>) {
  const [items, setItems] = useState<T[]>([]);
  const [searchItems, setSearchItems] = useState<T[]>([]);
  const [searchValue, setSearchValue] = useState("");
  const [loading, setLoading] = useState(false);

  // initial load
  useEffect(() => {
    let mounted = true;

    void (async () => {
      setLoading(true);
      const res = await fetchAll();

      if (mounted) {
        setItems(res || []);
        setLoading(false);
      }
    })();

    return () => {
      mounted = false;
    };
  }, []);

  // search with debounce
  useEffect(() => {
    let mounted = true;
    const timeout = setTimeout(() => {
      void (async () => {
        if (!searchValue) {
          setSearchItems([]);
          return;
        }

        setLoading(true);
        const res = await searchFn(searchValue);

        if (mounted) {
          setSearchItems(res || []);
          setLoading(false);
        }
      })();
    }, debounceMs);

    return () => {
      mounted = false;
      clearTimeout(timeout);
    };
  }, [searchValue]);

  const displayItems = searchValue && searchItems.length > 0
    ? searchItems
    : items;

  return {
    items,
    searchItems,
    searchValue,
    setSearchValue,
    displayItems,
    loading,
    setItems,
  };
}